import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageService, SharedModule } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { map, startWith } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { ApplicationProfileService } from '@shared/services/application-profile.service';
import { Representative } from '@shared/models';
import { RepresentativeFormComponent } from '@app/areas/admin/representatives/forms/representative-form/representative-form.component';
import { RepresentativeMerchantsCardComponent } from '@app/areas/admin/representatives/components/representative-merchants-card/representative-merchants-card.component';
import { RepresentativeFormService } from '@app/areas/admin/representatives/services/representative-form.service';
import { RepresentativesApiService } from '@app/areas/admin/representatives/services/representatives.api.service';

@Component({
  selector: 'app-representative-detail',
  templateUrl: './representative-detail.component.html',
  styleUrls: ['./representative-detail.component.scss'],
  standalone: true,
  imports: [
    RepresentativeFormComponent,
    RepresentativeMerchantsCardComponent,
    ButtonModule,
    CardModule,
    SharedModule,
    AsyncPipe,
    NgIf,
  ],
})
export class RepresentativeDetailComponent implements OnInit, OnDestroy {
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly representativeFormService = inject(RepresentativeFormService);
  private readonly representativesApiService = inject(RepresentativesApiService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected representative: Representative | null = null;
  protected notFound = false;
  protected isSaving = false;

  protected mode$ = this.representativeFormService.mode$;
  protected isSaveDisabled$ = this.representativeFormService.getForm().statusChanges.pipe(
    startWith(this.representativeFormService.getForm().status),
    map((status) => status !== 'VALID')
  );

  private subs = new Subscription();

  ngOnInit(): void {
    this.subs.add(
      this.route.paramMap
        .pipe(map((params) => Number(params.get('representativeId'))))
        .subscribe((representativeId) => this.loadRepresentative(representativeId))
    );

    this.subs.add(
      this.representativesApiService.representativeChanged$.subscribe((representative) => {
        // Ignore pushes for a different representative, and don't clobber an in-progress edit.
        if (
          this.representative?.RepresentativeId !== representative.RepresentativeId ||
          this.representativeFormService.getMode() === 'edit'
        ) {
          return;
        }

        this.representative = representative;
        this.representativeFormService.initializeForView(representative);
      })
    );
  }

  private loadRepresentative(representativeId: number): void {
    this.subs.add(
      this.representativesApiService.getById(representativeId).subscribe((representative) => {
        if (!representative) {
          this.notFound = true;
          return;
        }

        this.notFound = false;
        this.representative = representative;
        this.representativeFormService.initializeForView(representative);
      })
    );
  }

  protected viewMerchant(merchantId: number): void {
    const coalitionId = this.applicationProfileService.getApplicationProfile().Representative.CoalitionId;
    this.router.navigate(['/', coalitionId, 'merchants', merchantId]);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected edit(): void {
    if (this.representative) {
      this.representativeFormService.initializeForEdit(this.representative);
    }
  }

  protected cancelEdit(): void {
    if (this.representative) {
      this.representativeFormService.initializeForView(this.representative);
    }
  }

  protected saveRepresentative(): void {
    const form = this.representativeFormService.getForm();
    if (!form.valid || !this.representative) {
      return;
    }

    this.isSaving = true;
    const formValue = form.getRawValue();

    this.subs.add(
      this.representativesApiService
        .update(this.representative.RepresentativeId, {
          NameFirst: formValue.NameFirst!,
          NameLast: formValue.NameLast!,
          PhoneNumber: formValue.PhoneNumber?.replace(/\+/g, '') ?? undefined,
          AccessType: formValue.AccessType ?? undefined,
          RepresentativeType: formValue.RepresentativeType ?? undefined,
          LanguageType: formValue.LanguageType ?? undefined,
        })
        .subscribe((representative) => {
          this.isSaving = false;
          if (representative) {
            this.representative = representative;
            this.representativeFormService.initializeForView(representative);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: Update Representative',
              detail: 'Something went wrong while updating the representative',
            });
          }
        })
    );
  }
}
